import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { CiCamera, CiVideoOff } from "react-icons/ci";
const LocalCameraPreview = forwardRef((props, ref) => {
    const { isCameraOn, setIsCameraOn, initialMirror = true, mediaStream = null, permissionsGranted = false } = props;
    const videoRef = useRef(null);
    const streamRef = useRef(null);
    const [isStreaming, setIsStreaming] = useState(false);
    const [isMuted, setIsMuted] = useState(true);
    const [mirror, setMirror] = useState(initialMirror);
    const [devices, setDevices] = useState([]);
    const [currentDeviceId, setCurrentDeviceId] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        let mounted = true;
        navigator.mediaDevices?.enumerateDevices()
            .then((devs) => {
                if (!mounted) return;
                const videoInputs = devs.filter(d => d.kind === "videoinput");
                setDevices(videoInputs);
            })
            .catch((err) => console.warn("enumerateDevices failed:", err));

        return () => { mounted = false; stopStream(); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    async function startStream({ deviceId = null } = {}) {
        setError(null);
        stopStream();
        const constraints = {
            audio: { echoCancellation: true, noiseSuppression: true },
            video: deviceId
                ? { deviceId: { exact: deviceId } }
                : { facingMode: { ideal: "user" }, width: { ideal: 1280 }, height: { ideal: 720 } },
        };
        try {
            const stream = await navigator.mediaDevices.getUserMedia(constraints);
            streamRef.current = stream;
            if (videoRef.current) {
                videoRef.current.srcObject = stream;
                const playPromise = videoRef.current.play();
                if (playPromise !== undefined) {
                    playPromise.catch((e) => console.warn("Video play prevented:", e));
                }
                videoRef.current.muted = isMuted;
                videoRef.current.playsInline = true;
            }
            setIsStreaming(true);
            const videoTrack = stream.getVideoTracks()[0];
            if (videoTrack?.getSettings) {
                const settings = videoTrack.getSettings();
                if (settings.deviceId) setCurrentDeviceId(settings.deviceId);
            }
            const devs = await navigator.mediaDevices.enumerateDevices();
            setDevices(devs.filter(d => d.kind === "videoinput"));
        } catch (err) {
            console.error("getUserMedia error:", err);
            setError(err.message || String(err));
            setIsStreaming(false);
        }
    }

    function attachStream(stream) {
        streamRef.current = stream;
        if (videoRef.current) {
            videoRef.current.srcObject = stream;
            const playPromise = videoRef.current.play();
            if (playPromise !== undefined) {
                playPromise.catch((e) => console.warn("Video play prevented:", e));
            }
            videoRef.current.muted = isMuted;
            videoRef.current.playsInline = true;
        }
        setIsStreaming(true);
        const videoTrack = stream.getVideoTracks()[0];
        if (videoTrack?.getSettings) {
            const settings = videoTrack.getSettings();
            if (settings.deviceId) setCurrentDeviceId(settings.deviceId);
            setIsCameraOn(videoTrack.enabled);
        } else {
            setIsCameraOn(false);
        }
    }

    function stopStream() {
        const s = streamRef.current;
        if (s) {
            s.getTracks().forEach((t) => t.stop());
            streamRef.current = null;
        }
        if (videoRef.current) videoRef.current.srcObject = null;
        setIsStreaming(false);
        setCurrentDeviceId(null);
    }

    async function switchCamera() {
        if (!devices || devices.length <= 1) {
            const devs = await navigator.mediaDevices.enumerateDevices();
            setDevices(devs.filter(d => d.kind === "videoinput"));
            return;
        }
        const currentIndex = devices.findIndex(d => d.deviceId === currentDeviceId);
        const nextIndex = (currentIndex + 1) % devices.length;
        await startStream({ deviceId: devices[nextIndex].deviceId });
    }

    useImperativeHandle(ref, () => ({
        cameraSwitch() {
            if (!streamRef.current) return;
            const videoTrack = streamRef.current.getVideoTracks()[0];
            if (!videoTrack) return;
            videoTrack.enabled = !videoTrack.enabled;

            // Re-attach stream to video element when turning camera back on
            if (videoTrack.enabled && videoRef.current) {
                videoRef.current.srcObject = streamRef.current;
                videoRef.current.play().catch(() => { });
            }

            setIsCameraOn(videoTrack.enabled);
        },
    }));

    useEffect(() => {
        if (mediaStream) {
            attachStream(mediaStream);
            return;
        }
        if (permissionsGranted) {
            startStream({ deviceId: currentDeviceId });
        } else {
            stopStream();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mediaStream, permissionsGranted]);

    return (
        <>
            <style>{`
                .lcp-wrapper {
                    position: absolute;
                    bottom: 100px;
                    right: 16px;
                    width: 120px;
                    height: 160px;
                    border-radius: 20px;
                    overflow: hidden;
                    box-shadow:
                        0 8px 32px rgba(120, 80, 200, 0.25),
                        0 2px 8px rgba(0, 0, 0, 0.15),
                        inset 0 0 0 1.5px rgba(255, 255, 255, 0.5);
                    background: linear-gradient(135deg, rgba(200, 180, 255, 0.4), rgba(160, 140, 220, 0.3));
                    backdrop-filter: blur(12px);
                    -webkit-backdrop-filter: blur(12px);
                    transition: transform 0.2s ease, box-shadow 0.2s ease;
                    cursor: pointer;
                    z-index: 10;
                }

                .lcp-wrapper:hover {
                    transform: scale(1.03) translateY(-2px);
                    box-shadow:
                        0 12px 40px rgba(120, 80, 200, 0.35),
                        0 4px 12px rgba(0, 0, 0, 0.2),
                        inset 0 0 0 1.5px rgba(255, 255, 255, 0.65);
                }

                .lcp-wrapper:active {
                    transform: scale(0.98);
                }

                .lcp-video {
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                    display: block;
                }

                /* Subtle inner vignette for depth */
                .lcp-wrapper::after {
                    content: '';
                    position: absolute;
                    inset: 0;
                    border-radius: 20px;
                    box-shadow: inset 0 0 20px rgba(80, 40, 140, 0.18);
                    pointer-events: none;
                    z-index: 2;
                }

                /* Top shimmer line */
                .lcp-wrapper::before {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: 15%;
                    right: 15%;
                    height: 1px;
                    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.8), transparent);
                    z-index: 3;
                    pointer-events: none;
                }

                .lcp-placeholder {
                    position: absolute;
                    inset: 0;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    gap: 10px;
                    background: linear-gradient(145deg,
                        rgba(210, 190, 255, 0.6),
                        rgba(180, 155, 240, 0.5),
                        rgba(155, 120, 220, 0.4)
                    );
                    z-index: 1;
                }

                .lcp-camera-icon-wrap {
                    width: 44px;
                    height: 44px;
                    border-radius: 50%;
                    background: rgba(255, 255, 255, 0.35);
                    backdrop-filter: blur(8px);
                    -webkit-backdrop-filter: blur(8px);
                    border: 1.5px solid rgba(255, 255, 255, 0.6);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: background 0.2s ease, transform 0.2s ease;
                    box-shadow: 0 2px 12px rgba(120, 80, 200, 0.2);
                }

                .lcp-camera-icon-wrap:hover {
                    background: rgba(255, 255, 255, 0.5);
                    transform: scale(1.08);
                }

                .lcp-camera-icon-wrap svg {
                    width: 22px;
                    height: 22px;
                    color: rgba(90, 50, 160, 0.85);
                }

                .lcp-label {
                    font-size: 10px;
                    font-weight: 500;
                    letter-spacing: 0.04em;
                    color: rgba(80, 40, 140, 0.75);
                    text-align: center;
                    line-height: 1.3;
                }

                /* Camera off overlay */
                .lcp-cam-off-overlay {
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, rgb(175, 0, 86), rgb(255, 77, 166));
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2;
    border-radius: 20px;
    transition: opacity 0.3s ease;
                }

                .lcp-cam-off-dot {
                    width: 8px;
                    height: 8px;
                    border-radius: 50%;
                    background: rgba(255, 255, 255, 0.5);
                }

                /* Subtle live pulse indicator */
                @keyframes lcp-pulse {
                    0%, 100% { opacity: 1; transform: scale(1); }
                    50% { opacity: 0.6; transform: scale(0.85); }
                }

                .lcp-live-dot {
                    position: absolute;
                    top: 10px;
                    left: 10px;
                    width: 7px;
                    height: 7px;
                    border-radius: 50%;
                    background: #7CFC9A;
                    box-shadow: 0 0 6px rgba(124, 252, 154, 0.8);
                    animation: lcp-pulse 2s ease-in-out infinite;
                    z-index: 4;
                }
            `}</style>

            <div className="lcp-wrapper" style={{ display: isCameraOn ? 'block' : 'none' }}>
                {/* Live green dot when streaming and camera on */}
                {isStreaming && isCameraOn && (
                    <div className="lcp-live-dot" aria-hidden="true" />
                )}

                {/* Video element */}
                <video
                    ref={videoRef}
                    className="lcp-video"
                    style={{
                        transform: mirror ? "scaleX(-1)" : "none",
                    }}
                    autoPlay
                    playsInline
                    muted={isMuted}
                />

                {/* Camera off placeholder */}
                {/* {!isCameraOn && (
                    <div className="lcp-cam-off-overlay">
                        <CiVideoOff style={{ width: 28, height: 28, color: 'rgba(255,255,255,0.75)' }} />
                    </div>
                )} */}

                {/* Placeholder when not streaming */}
                {!isCameraOn && (
                    <div className="lcp-placeholder">
                        <button
                            className="lcp-camera-icon-wrap"
                            onClick={() => permissionsGranted && startStream({ deviceId: currentDeviceId })}
                            aria-label="Start camera"
                        >
                            <CiCamera />
                        </button>
                        <span className="lcp-label">
                            {error ? "Camera\nunavailable" : "Tap to\nstart"}
                        </span>
                    </div>
                )}
            </div>
        </>
    );
});

export default LocalCameraPreview;